import type { EmployeeClockState } from './timeClockActions'

const EMPLOYEE_PREFIX = 'rbcfs.myDay.employee.v1.'
const JOBS_PREFIX = 'rbcfs.myDay.jobs.v1.'
const CLOCK_PREFIX = 'rbcfs.myDay.clock.v1.'

export type CachedMyDayJob = {
  id: string
  job_number: string
  title: string
  status: string
  scheduled_start_time?: string | null
  scheduled_end_time?: string | null
  contract_id?: string | null
  service_type?: string | null
  dsny_integration?: boolean | null
  dsny_pickup_days?: string[] | null
  customer?: {
    company_name?: string | null
    contact_first_name?: string | null
    contact_last_name?: string | null
  } | null
  property?: {
    name?: string | null
    address?: string | null
    latitude?: number | null
    longitude?: number | null
  } | null
}

function readJson<T>(key: string): T | null {
  try {
    const raw = localStorage.getItem(key)
    if (!raw) return null
    return JSON.parse(raw) as T
  } catch {
    return null
  }
}

function writeJson(key: string, value: unknown) {
  try {
    localStorage.setItem(key, JSON.stringify(value))
  } catch {
    /* quota / private mode */
  }
}

function removeKey(key: string) {
  try {
    localStorage.removeItem(key)
  } catch {
    /* private mode */
  }
}

export function readCachedEmployeeId(userId: string): string | null {
  if (!userId) return null
  const row = readJson<{ userId: string; employeeId: string }>(`${EMPLOYEE_PREFIX}${userId}`)
  if (!row?.employeeId || row.userId !== userId) return null
  return row.employeeId
}

export function cacheEmployeeId(userId: string, employeeId: string) {
  if (!userId || !employeeId) return
  writeJson(`${EMPLOYEE_PREFIX}${userId}`, { userId, employeeId })
}

export function readCachedMyDayJobs(
  employeeId: string,
  dateYmd: string
): CachedMyDayJob[] | null {
  if (!employeeId || !dateYmd) return null
  const row = readJson<{ employeeId: string; dateYmd: string; jobs: CachedMyDayJob[] }>(
    `${JOBS_PREFIX}${employeeId}.${dateYmd}`
  )
  if (!row || row.employeeId !== employeeId || row.dateYmd !== dateYmd) return null
  if (!Array.isArray(row.jobs) || row.jobs.length === 0) return null
  return row.jobs
}

export function cacheMyDayJobs(employeeId: string, dateYmd: string, jobs: CachedMyDayJob[]) {
  if (!employeeId || !dateYmd) return
  if (!Array.isArray(jobs) || jobs.length === 0) {
    clearMyDayJobsCache(employeeId, dateYmd)
    return
  }
  writeJson(`${JOBS_PREFIX}${employeeId}.${dateYmd}`, { employeeId, dateYmd, jobs })
}

export function clearMyDayJobsCache(employeeId: string, dateYmd: string) {
  if (!employeeId || !dateYmd) return
  removeKey(`${JOBS_PREFIX}${employeeId}.${dateYmd}`)
}

export function readCachedMyDayClock(employeeId: string): EmployeeClockState | null {
  if (!employeeId) return null
  const row = readJson<{ employeeId: string; clock: EmployeeClockState }>(
    `${CLOCK_PREFIX}${employeeId}`
  )
  if (!row || row.employeeId !== employeeId || !row.clock) return null
  return row.clock
}

export function cacheMyDayClock(employeeId: string, clock: EmployeeClockState) {
  if (!employeeId) return
  writeJson(`${CLOCK_PREFIX}${employeeId}`, { employeeId, clock })
}
export function purgeLegacyMyDayJobCaches() {
  try {
    for (let i = localStorage.length - 1; i >= 0; i -= 1) {
      const key = localStorage.key(i)
      if (!key || !key.startsWith(JOBS_PREFIX)) continue
      const raw = localStorage.getItem(key)
      if (!raw) {
        localStorage.removeItem(key)
        continue
      }
      try {
        const row = JSON.parse(raw) as { jobs?: unknown[] }
        if (!Array.isArray(row.jobs) || row.jobs.length === 0) localStorage.removeItem(key)
      } catch {
        localStorage.removeItem(key)
      }
    }
  } catch {
    /* private mode */
  }
}